<template>
    <group-report-template  v-model:current-report-template-id="currentReportTemplateId"
                            :template-id="templateId"
                            v-model:for-group="forGroup"
                            v-model:interval="interval"
                            v-model:ran-report="reportSuccessfullySent"
                            v-model:reload-data="reloadTable"
                            v-model:running-errors="errorSending"
                            v-model:running-report="runningReport"
                            v-model:settings="settings"
                            :can-edit="permissions.can('manage_alarm_resolution_report')"
                            :users="users"
                            :default-settings="defaultSettings"
                            :prepare-settings-function="prepareSettings"
                            :hide-title="true"
                            :use-settings-drawer="true"
                            :use-template-cards="true"
                            :use-group-reporting-option="groupReportingEnabled"
                            :has-settings="hasManualRunSettings"
                            :report-icon="settingsSchema.icon"
                            :schema="settingsSchema"
                            report-name="alarm-resolution-report"
                            v-model:display-section="displaySection"
                            @useStandardReporting="setUseStandardReporting"
    >

        <template   #create-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="true"
                                    :timezone="timezone"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #edit-settings>
            <report-settings-panel  v-model="settings"
                                    v-model:interval="interval"
                                    :schema="settingsSchema"
                                    :is-savable="displaySection === 'show-template' || useStandardReporting"
                                    :timezone="timezone"
                                    :context="settingsContext"
            ></report-settings-panel>
        </template>

        <template   #run-options="props">
            <!-- Alarm Resolution runs over an explicit date range and has no
                 point selection, so it uses the SHARED range selector
                 (IntervalSelection with use-range, no meters) -- the same
                 "Reporting Period" control Excursion uses -- rather than its own
                 date pickers, so the range UX is identical across reports.
                 start/end are posted via extraParams. -->
            <interval-selection   :use-range="! props.reportTemplateId"
                                        :use-meters="false"
                                        :wide="true"
                                        :footer-mode="props.footerMode ?? 'none'"
                                        v-model:range-start="rangeStart"
                                        v-model:range-end="rangeEnd"
                                        :min-date="minDate"
                                        :max-date="maxDate"
                                        :max-span-days="maxRangeDays"
                                        :is-running="runningReport"
                                        :ran-report="reportSuccessfullySent"
                                        :running-errors="errorSending"
                                        :timezone="timezone"
                                        @run-report="runReport(props.reportTemplateId ?? null)"
            ></interval-selection>
        </template>
    </group-report-template>
</template>

<script>
import {ref} from 'vue'

import {SonicuCoreMixin} from "../../mixins/SonicuCoreMixin";
import {ajaxTableMixin} from "../../mixins/ajaxTableMixin";
import {useGroupReport} from "@/composables/useGroupReport";

import GroupReportTemplate from "./GroupReportTemplate";
import IntervalSelection from "./IntervalSelection";
import ReportSettingsPanel from "./settings/ReportSettingsPanel.vue";
import alarmResolutionSettingsSchema from "./settings/schemas/alarm-resolution";
import {toReportDate} from "./settings/ReportDateRange.vue";

export default {
    name: "GroupAlarmResolutionReport",
    mixins: [SonicuCoreMixin, ajaxTableMixin],
    components: {
        GroupReportTemplate,
        IntervalSelection,
        ReportSettingsPanel,
    },
    props: {
        // null on the report page; 'new' or a group report id on its New or
        // Edit page.
        templateId: {
            type: [String, Number],
            default: null,
        },
        interval: {
            type: String,
            default: 'monthly',
        },
        minDate: {
            type: [Date, String],
            default: () => new Date(),
        },
        maxDate: {
            type: [Date, String],
            default: () => new Date(),
        },
        defaultSettings: {
            type: Object,
            default() {
                return {}
            }
        },
        // The controller's MAX_RANGE_DAYS, so the run form allows the same
        // range the server accepts.
        maxRangeDays: {
            type: Number,
            default: 31,
        },
        users: {
            type: Object,
            default() {
                return {}
            }
        },
        timezone: {
            type: String,
            default: 'America/New_York',
        },
    },
    setup(props) {
        // The explicit start/end range from the shared range selector
        // (IntervalSelection with use-range). The composable's base params
        // post `end` and `meters`; extraParams overrides `end` with the range's
        // end and adds `start`. toReportDate posts a plain date so the day the
        // user picked cannot be shifted by a UTC conversion in transit. The
        // controller ignores the empty `meters`.
        const rangeStart = ref(null)
        const rangeEnd = ref(null)

        // The composable supplies settingsContext, hasManualRunSettings, and
        // prepareSettings (weekday default) -- this schema has no permission-gated
        // fields, so its usePermissions-backed context is correct here.
        const report = useGroupReport(alarmResolutionSettingsSchema, {
            interval: props.interval,
            defaultSettings: props.defaultSettings,
            weekdayDefault: 1,
            // A saved template's manual run posts no start, so the controller
            // derives the window from the template's interval, the same window
            // its scheduled sends cover.
            extraParams: (state, reportTemplateId) => {
                if (reportTemplateId !== null) {
                    return {}
                }

                return {
                    start: toReportDate(rangeStart.value, props.timezone),
                    end: toReportDate(rangeEnd.value, props.timezone) ?? state.endOfInterval.value,
                }
            },
        })

        return {
            ...report,
            rangeStart,
            rangeEnd,
        }
    },
    computed: {
        settingsSchema() {
            return alarmResolutionSettingsSchema
        },
    },
}
</script>
